import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Highlighter, Square, Eraser, MousePointer2, ZoomIn, ZoomOut } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import {
  bakePdfHighlights,
  createHighlightId,
  DEFAULT_HIGHLIGHT_COLOR,
  HIGHLIGHT_PALETTE,
  mergeNormalizedHighlightRects
} from '../../helpers/pdfHighlight';
import './PdfHighlightViewer.scss';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MODES = {
  BROWSE: 'browse',
  HIGHLIGHT: 'highlight',
  DRAW: 'draw',
  ERASE: 'erase'
};

const PAGE_GUTTER = 16;

const modeButtonStyle = active => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.2,
  background: active ? '#1f2937' : 'transparent',
  color: active ? '#fff' : '#374151',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.18)' : 'none'
});

/** pdf.js endOfContent helper — limits selection jumps between absolutely positioned spans. */
const setEndOfContentActive = (textLayer, event, active) => {
  const end = textLayer?.querySelector('.endOfContent');
  if (!end) return;
  if (!active) {
    end.style.top = '';
    end.classList.remove('active');
    return;
  }
  let adjustTop = event.target !== textLayer;
  adjustTop &&= getComputedStyle(end).getPropertyValue('-moz-user-select') !== 'none';
  if (adjustTop) {
    const bounds = textLayer.getBoundingClientRect();
    const ratio = Math.max(0, (event.pageY - bounds.top) / bounds.height);
    end.style.top = `${(ratio * 100).toFixed(2)}%`;
  }
  end.classList.add('active');
};

const PdfPage = ({
  pdf,
  pageNumber,
  scale,
  mode,
  color,
  highlights,
  enableAnnotations,
  onAddHighlights,
  onRemoveHighlight
}) => {
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const overlayRef = useRef(null);
  const renderTaskRef = useRef(null);
  const dragStartRef = useRef(null);

  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [draftRect, setDraftRect] = useState(null);

  const pageIndex = pageNumber - 1;
  const pageHighlights = (highlights || []).filter(h => h.pageIndex === pageIndex);
  const textSelectable = mode === MODES.HIGHLIGHT;

  useEffect(() => {
    let cancelled = false;

    const renderPage = async () => {
      const canvas = canvasRef.current;
      const textLayerEl = textLayerRef.current;
      if (!pdf || !canvas || !textLayerEl || !scale) return;

      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      if (pdf.loadingTask?.destroyed) return;

      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        setPageSize({ width: viewport.width, height: viewport.height });

        const context = canvas.getContext('2d');
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        if (cancelled) return;

        textLayerEl.innerHTML = '';
        textLayerEl.style.width = `${viewport.width}px`;
        textLayerEl.style.height = `${viewport.height}px`;
        textLayerEl.style.setProperty('--scale-factor', String(viewport.scale));

        const textContent = await page.getTextContent();
        await pdfjsLib.renderTextLayer({
          textContentSource: textContent,
          container: textLayerEl,
          viewport
        }).promise;
        if (cancelled) return;

        const endOfContent = document.createElement('div');
        endOfContent.className = 'endOfContent';
        textLayerEl.append(endOfContent);
      } catch (err) {
        // A destroyed document means the viewer moved on; the render is irrelevant.
        if (err?.name === 'RenderingCancelledException' || pdf.loadingTask?.destroyed) return;
        console.error('Failed to render PDF page', err);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, pageNumber, scale]);

  const getOverlayPoint = event => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
    };
  };

  const handleTextSelection = () => {
    setEndOfContentActive(textLayerRef.current, null, false);
    if (!enableAnnotations || mode !== MODES.HIGHLIGHT) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !textLayerRef.current) return;

    const textLayerRect = textLayerRef.current.getBoundingClientRect();
    if (!textLayerRect.width || !textLayerRect.height) return;

    let range;
    try {
      range = selection.getRangeAt(0);
    } catch (err) {
      return;
    }

    const spanRects = [];
    textLayerRef.current.querySelectorAll('span').forEach(span => {
      if (!selection.containsNode(span, true)) return;
      const r = span.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return;
      const midX = r.left + r.width / 2;
      const midY = r.top + r.height / 2;
      if (
        midX < textLayerRect.left ||
        midX > textLayerRect.right ||
        midY < textLayerRect.top ||
        midY > textLayerRect.bottom
      ) {
        return;
      }
      spanRects.push(r);
    });

    const sourceRects = spanRects.length ? spanRects : Array.from(range.getClientRects());
    if (!sourceRects.length) return;

    const pageRects = mergeNormalizedHighlightRects(
      sourceRects
        .map(r => ({
          x: (r.left - textLayerRect.left) / textLayerRect.width,
          y: (r.top - textLayerRect.top) / textLayerRect.height,
          width: r.width / textLayerRect.width,
          height: r.height / textLayerRect.height
        }))
        .filter(h => h.width > 0.001 && h.height > 0.001)
    ).map(r => ({
      id: createHighlightId(),
      pageIndex,
      type: 'text',
      color,
      ...r
    }));

    if (pageRects.length) onAddHighlights(pageRects);
    selection.removeAllRanges();
  };

  const onOverlayMouseDown = e => {
    if (!enableAnnotations || mode !== MODES.DRAW) return;
    if (e.button !== 0) return;
    e.preventDefault();
    const point = getOverlayPoint(e);
    if (!point) return;
    dragStartRef.current = point;
    setDraftRect({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const onOverlayMouseMove = e => {
    if (!dragStartRef.current) return;
    const point = getOverlayPoint(e);
    if (!point) return;
    const start = dragStartRef.current;
    setDraftRect({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y)
    });
  };

  const cancelDraft = () => {
    dragStartRef.current = null;
    setDraftRect(null);
  };

  const onOverlayMouseUp = () => {
    if (!dragStartRef.current || !draftRect) {
      cancelDraft();
      return;
    }
    if (draftRect.width > 0.005 && draftRect.height > 0.005) {
      onAddHighlights([
        {
          id: createHighlightId(),
          pageIndex,
          type: 'rect',
          color,
          ...draftRect
        }
      ]);
    }
    cancelDraft();
  };

  return (
    <div
      id={`pdf-page-${pageNumber}`}
      className="pdf-page position-relative bg-white shadow-sm"
      style={{
        width: pageSize.width || '100%',
        height: pageSize.height || undefined,
        minHeight: pageSize.height ? undefined : 200,
        margin: `0 auto ${PAGE_GUTTER}px`
      }}
    >
      <canvas ref={canvasRef} className="d-block" />

      {/* Text layer is only interactive in Highlight mode (creating annotations). */}
      <div
        ref={textLayerRef}
        className="textLayer"
        onMouseDown={e => {
          if (e.button !== 0 || !textSelectable) return;
          setEndOfContentActive(textLayerRef.current, e, true);
        }}
        onMouseUp={handleTextSelection}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          lineHeight: 1,
          zIndex: 2,
          pointerEvents: textSelectable ? 'auto' : 'none',
          userSelect: textSelectable ? 'text' : 'none',
          WebkitUserSelect: textSelectable ? 'text' : 'none'
        }}
      />

      <div
        ref={overlayRef}
        onMouseDown={onOverlayMouseDown}
        onMouseMove={onOverlayMouseMove}
        onMouseUp={onOverlayMouseUp}
        onMouseLeave={cancelDraft}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          zIndex: 3,
          cursor:
            mode === MODES.DRAW
              ? 'crosshair'
              : mode === MODES.ERASE
              ? 'pointer'
              : mode === MODES.HIGHLIGHT
              ? 'text'
              : 'default',
          pointerEvents: textSelectable ? 'none' : 'auto'
        }}
      >
        {pageHighlights.map(h => (
          <div
            key={h.id}
            title={mode === MODES.ERASE ? 'Click to erase' : undefined}
            onClick={e => {
              e.stopPropagation();
              if (mode === MODES.ERASE) onRemoveHighlight(h.id);
            }}
            style={{
              position: 'absolute',
              left: `${h.x * 100}%`,
              top: `${h.y * 100}%`,
              width: `${h.width * 100}%`,
              height: `${h.height * 100}%`,
              backgroundColor: h.color || DEFAULT_HIGHLIGHT_COLOR,
              opacity: mode === MODES.ERASE ? 0.5 : 0.38,
              outline: mode === MODES.ERASE ? '1px dashed #111827' : 'none',
              boxSizing: 'border-box',
              pointerEvents: mode === MODES.ERASE || mode === MODES.DRAW ? 'auto' : 'none',
              mixBlendMode: 'multiply'
            }}
          />
        ))}
        {draftRect && (
          <div
            style={{
              position: 'absolute',
              left: `${draftRect.x * 100}%`,
              top: `${draftRect.y * 100}%`,
              width: `${draftRect.width * 100}%`,
              height: `${draftRect.height * 100}%`,
              backgroundColor: color,
              opacity: 0.35,
              outline: '1px dashed #333',
              pointerEvents: 'none',
              mixBlendMode: 'multiply'
            }}
          />
        )}
      </div>
    </div>
  );
};

const PdfHighlightViewer = ({
  src,
  highlights = [],
  onHighlightsChange,
  enableAnnotations = false
}) => {
  const containerRef = useRef(null);
  const docCacheRef = useRef(null);
  const basePageWidthRef = useRef(0);
  const bakedBrowseUrlRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pdf, setPdf] = useState(null);
  const [fitScale, setFitScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [mode, setMode] = useState(MODES.BROWSE);
  const [color, setColor] = useState(DEFAULT_HIGHLIGHT_COLOR);
  const [browseSrc, setBrowseSrc] = useState(src);
  const [browsePreparing, setBrowsePreparing] = useState(false);

  const scale = fitScale * zoom;
  const isBrowseMode = !enableAnnotations || mode === MODES.BROWSE;
  const isAnnotating = enableAnnotations && !isBrowseMode;

  const updateHighlights = useCallback(
    next => {
      if (onHighlightsChange) onHighlightsChange(next);
    },
    [onHighlightsChange]
  );

  const addHighlights = useCallback(
    rects => {
      updateHighlights([...(highlights || []), ...rects]);
    },
    [highlights, updateHighlights]
  );

  const removeHighlight = useCallback(
    id => {
      updateHighlights((highlights || []).filter(h => h.id !== id));
    },
    [highlights, updateHighlights]
  );

  const updateFitScale = useCallback(() => {
    const el = containerRef.current;
    const baseWidth = basePageWidthRef.current;
    if (!el || !baseWidth) return;
    const available = Math.max(el.clientWidth - PAGE_GUTTER * 2, 120);
    setFitScale(available / baseWidth);
  }, []);

  const revokeBakedBrowseUrl = useCallback(() => {
    if (bakedBrowseUrlRef.current) {
      URL.revokeObjectURL(bakedBrowseUrlRef.current);
      bakedBrowseUrlRef.current = null;
    }
  }, []);

  // Browse = native PDF iframe (smooth select/copy). Bake highlights so they stay visible.
  useEffect(() => {
    if (!isBrowseMode || !src) return undefined;

    let cancelled = false;

    const prepareBrowseSrc = async () => {
      if (!(highlights || []).length) {
        revokeBakedBrowseUrl();
        setBrowseSrc(src);
        setBrowsePreparing(false);
        return;
      }

      setBrowsePreparing(true);
      try {
        const bakedBlob = await bakePdfHighlights(src, highlights);
        if (cancelled) return;
        revokeBakedBrowseUrl();
        const url = URL.createObjectURL(bakedBlob);
        bakedBrowseUrlRef.current = url;
        setBrowseSrc(url);
      } catch (err) {
        console.error('Failed to prepare browse PDF with highlights', err);
        if (!cancelled) setBrowseSrc(src);
      } finally {
        if (!cancelled) setBrowsePreparing(false);
      }
    };

    prepareBrowseSrc();
    return () => {
      cancelled = true;
    };
  }, [isBrowseMode, src, highlights, revokeBakedBrowseUrl]);

  useEffect(() => () => revokeBakedBrowseUrl(), [revokeBakedBrowseUrl]);

  const releaseDoc = useCallback(() => {
    const cached = docCacheRef.current;
    docCacheRef.current = null;
    cached?.promise.then(doc => doc.destroy()).catch(() => {});
  }, []);

  // Load pdf.js when entering annotate modes; one document per src, kept alive while
  // toggling back to Browse so re-entering Highlight is instant.
  useEffect(() => {
    if (!isAnnotating || !src) return undefined;

    if (docCacheRef.current?.src !== src) {
      // Drop pages of the previous source in the same commit as the destroy, so no
      // render can still be in flight against a destroyed document.
      setPdf(null);
      setPageCount(0);
      setZoom(1);
      releaseDoc();
      const loadingTask = pdfjsLib.getDocument({ url: src, withCredentials: false });
      docCacheRef.current = { src, promise: loadingTask.promise, doc: null };
    }

    const entry = docCacheRef.current;
    const { promise } = entry;
    let cancelled = false;
    const isStale = () => cancelled || docCacheRef.current?.promise !== promise;

    const showDoc = async () => {
      setLoading(!entry.doc);
      setError(null);

      try {
        const doc = await promise;
        const firstPage = await doc.getPage(1);
        if (isStale()) return;

        entry.doc = doc;
        basePageWidthRef.current = firstPage.getViewport({ scale: 1 }).width;
        setPdf(doc);
        setPageCount(doc.numPages);

        requestAnimationFrame(() => {
          if (!isStale()) updateFitScale();
        });
      } catch (err) {
        if (isStale()) return;
        console.error('Failed to load PDF for annotation viewer', err);
        setError('Unable to load PDF preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    showDoc();

    return () => {
      cancelled = true;
    };
  }, [src, isAnnotating, updateFitScale, releaseDoc]);

  useEffect(() => () => releaseDoc(), [releaseDoc]);

  useEffect(() => {
    if (!isAnnotating) return undefined;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    // Defer to the next frame so resizing never re-enters the observer in the same
    // loop ("ResizeObserver loop completed with undelivered notifications").
    let frame = null;
    const observer = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateFitScale);
    });
    observer.observe(el);
    updateFitScale();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [updateFitScale, loading, pageCount, isAnnotating]);

  useEffect(() => {
    if (!enableAnnotations) return undefined;
    const onKeyDown = e => {
      if (e.key === 'Escape') setMode(MODES.BROWSE);
      if ((e.key === 'e' || e.key === 'E') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        setMode(MODES.ERASE);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enableAnnotations]);

  if (error && isAnnotating) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-danger">
        {error}
      </div>
    );
  }

  return (
    <div
      className={`d-flex flex-column h-100 w-100 pdf-highlight-viewer pdf-mode-${mode}`}
      style={{ minHeight: 0 }}
    >
      {enableAnnotations && (
        <div
          className="d-flex flex-wrap align-items-center justify-content-between px-2 py-2 bg-white border-bottom"
          style={{ gap: 10 }}
        >
          <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
            <div
              className="d-inline-flex align-items-center p-1 flex-shrink-0"
              style={{ background: '#f3f4f6', borderRadius: 10, gap: 2 }}
              role="toolbar"
              aria-label="Annotation modes"
            >
              <button
                type="button"
                title="Browse — native PDF select/copy"
                style={modeButtonStyle(mode === MODES.BROWSE)}
                onClick={() => setMode(MODES.BROWSE)}
              >
                <MousePointer2 size={15} />
                Browse
              </button>
              <button
                type="button"
                title="Highlight selectable text"
                style={modeButtonStyle(mode === MODES.HIGHLIGHT)}
                onClick={() => setMode(MODES.HIGHLIGHT)}
              >
                <Highlighter size={15} />
                Highlight
              </button>
              <button
                type="button"
                title="Draw rectangle highlight"
                style={modeButtonStyle(mode === MODES.DRAW)}
                onClick={() => setMode(MODES.DRAW)}
              >
                <Square size={15} />
                Area
              </button>
              <button
                type="button"
                title="Eraser — click a highlight to remove it"
                style={modeButtonStyle(mode === MODES.ERASE)}
                onClick={() => setMode(MODES.ERASE)}
              >
                <Eraser size={15} />
                Erase
              </button>
            </div>

            {(mode === MODES.HIGHLIGHT || mode === MODES.DRAW) && (
              <div
                className="d-inline-flex align-items-center flex-shrink-0"
                style={{
                  gap: 6,
                  padding: '4px 6px',
                  borderRadius: 10,
                  background: '#fafafa',
                  border: '1px solid #e5e7eb'
                }}
                role="listbox"
                aria-label="Highlight colors"
              >
                {HIGHLIGHT_PALETTE.map(swatch => {
                  const selected = color === swatch.value;
                  return (
                    <button
                      key={swatch.id}
                      type="button"
                      title={swatch.label}
                      aria-label={swatch.label}
                      aria-selected={selected}
                      onClick={() => setColor(swatch.value)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        border: selected ? '2px solid #111827' : '1px solid rgba(0,0,0,0.12)',
                        background: swatch.value,
                        boxShadow: selected ? '0 0 0 2px rgba(17,24,39,0.15)' : 'none',
                        padding: 0,
                        cursor: 'pointer'
                      }}
                    />
                  );
                })}
              </div>
            )}

            {mode === MODES.ERASE && (
              <span className="small text-muted text-nowrap">Click a highlight to erase</span>
            )}
            {mode === MODES.BROWSE && (
              <span className="small text-muted text-nowrap">Select Highlight or Area to annotate.</span>
            )}
          </div>

          {isAnnotating && (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <span className="small text-muted" style={{ minWidth: 52, textAlign: 'center' }}>
                {pageCount ? `${pageCount} pg` : '—'}
              </span>
              <Button
                size="sm"
                variant="outline-dark"
                disabled={zoom <= 0.6}
                onClick={() => setZoom(z => Math.max(0.6, Number((z - 0.1).toFixed(2))))}
              >
                <ZoomOut size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline-dark"
                disabled={zoom >= 2}
                onClick={() => setZoom(z => Math.min(2, Number((z + 0.1).toFixed(2))))}
              >
                <ZoomIn size={16} />
              </Button>
            </div>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className="flex-grow-1 position-relative bg-secondary pdf-scroll-container"
        style={{
          minHeight: 0,
          overflowX: isAnnotating && zoom > 1 ? 'auto' : 'hidden',
          overflowY: isBrowseMode ? 'hidden' : 'auto'
        }}
      >
        {isBrowseMode ? (
          browsePreparing ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <Spinner animation="border" variant="light" />
            </div>
          ) : (
            <iframe
              title="pdf-browse-preview"
              src={browseSrc}
              style={{
                position: 'absolute',
                border: 'none',
                width: '100%',
                height: '100%',
                left: 0,
                top: 0,
                colorScheme: 'normal'
              }}
            />
          )
        ) : loading || !pdf ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <Spinner animation="border" variant="light" />
          </div>
        ) : (
          <div
            className="pdf-scroll-inner"
            style={{
              padding: PAGE_GUTTER,
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {Array.from({ length: pageCount }, (_, i) => (
              <PdfPage
                key={`${src}-${i + 1}`}
                pdf={pdf}
                pageNumber={i + 1}
                scale={scale}
                mode={mode}
                color={color}
                highlights={highlights}
                enableAnnotations={enableAnnotations}
                onAddHighlights={addHighlights}
                onRemoveHighlight={removeHighlight}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfHighlightViewer;
